import { toast } from 'react-toastify'
import { SETTINGS_URL } from '../../../apiHelper/apiRoute/settings'
import { getFromSettingsServer } from '../../../apiHelper/settingsApi'

const CPT_INJECTION_CODES = [20600, 20604, 20605, 20606, 20610, 20611, 64405, 64450, 64461]
const validDupeCPTCodes = ['Q4239', 'Q4329', 'Q4271', '20610']

/**
 * Checks if the CPT codes selected for a visit require a modifier to be added
 *
 * @param visitDetails
 * @returns {boolean} whether a modifier is needed
 */
function doCPTsRequireModifier({ procedureCodes } = {}) {
  if (!procedureCodes) {
    return false
  }
  for (let i = 0; i < procedureCodes.length; i++) {
    const codeRequiresMod = doesCPTRequireModifier(procedureCodes[i])
    if (codeRequiresMod) {
      return true
    }
  }
  return false
}

/**
 * Checks if a single CPT Code needs a modifier
 *
 * @param code
 * @param modifiers
 * @returns {boolean}
 */
function doesCPTRequireModifier({ code, modifiers } = {}) {
  if (CPT_INJECTION_CODES.includes(Number(code))) {
    if (!modifiers || modifiers.length === 0) {
      return true
    }
  }
  return false
}

/**
 * Strings together CPT codes that need a modifier for display purposes
 * @param codes
 * @returns {String}
 */
function stringifyCPTCodesNeedingModifier(codes) {
  return codes
    .filter(doesCPTRequireModifier)
    .map((code) => code.code)
    .join(', ')
}

function updateCPTWithFSModifier(sharedServiceProvider, cpts, callback) {
  if (sharedServiceProvider) {
    // check to add FS to procedures
    let emCodeIndex = cpts.findIndex((code) => code.em_code === true)
    if (emCodeIndex > -1) {
      // add FS if not there
      let emCode = cpts[emCodeIndex]
      let hasFSModifier = emCode.modifiers?.includes('FS')
      if (!hasFSModifier) {
        // FS not there, add it
        let newProcs = [...cpts]
        newProcs[emCodeIndex] = {
          ...emCode,
          modifiers: ['FS', ...(emCode.modifiers || [])],
        }
        callback(newProcs)
      }
    }
  } else {
    // check to remove FS from procedures
    let emCodeIndex = cpts.findIndex((code) => code.em_code === true)
    if (emCodeIndex > -1) {
      let emCode = cpts[emCodeIndex]
      // check if FS is there
      if (emCode?.modifiers?.includes('FS')) {
        let newProcs = [...cpts]
        newProcs[emCodeIndex] = {
          ...emCode,
          modifiers: (emCode.modifiers || [])?.filter((mod) => mod !== 'FS'),
        }
        callback(newProcs)
      }
    }
  }
}

function addCPTVerification(procedureCodesSelected, item, setSelectedProcedure, options = {}) {
  let originalCPT = procedureCodesSelected.find((cpt) => cpt.code === item.code)
  if (!Boolean(originalCPT)) {
    if (item.em_code === true) {
      let em_exist = procedureCodesSelected.filter((code) => code.em_code === true)
      if (em_exist && em_exist.length > 0 && !options.ignoreEM) {
        toast.error(
          'Only one Evaluation & Management (E&M) code can be billed per encounter.  Please remove additional E&M codes',
        )
      } else if (em_exist && em_exist.length > 0 && options.ignoreEM) {
        setSelectedProcedure([
          item,
          ...procedureCodesSelected.filter((code) => code.em_code !== true),
        ])
      } else {
        setSelectedProcedure([item, ...procedureCodesSelected])
      }
    } else {
      setSelectedProcedure([...procedureCodesSelected, item])
    }
  } else {
    // if dupe CPT is emCode, dont let it be added
    if (!item.em_code) {
      // check modifiers are different
      let identicalModCPT = procedureCodesSelected.find(
        (cpt) =>
          cpt.code === item.code &&
          (cpt.modifiers || []).sort().join() === (item.modifiers || []).sort().join(),
      )
      if (!identicalModCPT && validDupeCPTCodes.includes(originalCPT.code)) {
        setSelectedProcedure([...procedureCodesSelected, item])
      }
    }
  }
}

export async function cptSearch({ searchString, pos, encounterType }) {
  try {
    if (searchString) {
      let cptSearchEndpoint = SETTINGS_URL.GET_CPT.replace('{searchString}', searchString)
        .replace('{recordCount}', '10')
        .replace('{pos}', pos || '')

      let cptSearchResult = await getFromSettingsServer(cptSearchEndpoint)
      if (cptSearchResult?.status === 'success') {
        return cptSearchResult.results || []
      } else {
        console.error('Error getting CPTs', cptSearchResult)
      }
    } else {
      // no string, so we can get the smart codes
      // which will be shown if there is no chosen template
      let smartCPTURL = SETTINGS_URL.GET_SMART_CPTS.replace('{POS}', pos || '').replace(
        '{ENC_TYPE}',
        encounterType?.toLowerCase() || '',
      )
      let cptSmartCodesResult = await getFromSettingsServer(smartCPTURL)
      if (cptSmartCodesResult?.status === 'success') {
        return cptSmartCodesResult.results || []
      } else {
        console.error('Error getting smart CPTs', cptSmartCodesResult)
      }
    }
  } catch (err) {
    console.error('getting error in rendering cptSearch', err)
  }

  return []
}

export {
  doCPTsRequireModifier,
  doesCPTRequireModifier,
  stringifyCPTCodesNeedingModifier,
  updateCPTWithFSModifier,
  addCPTVerification,
}
